import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
} from 'reactstrap';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { submitContactMessage } from '../../slices/contactMessages/thunk';
import { clearState } from '../../slices/contactMessages/reducer';

const ContactUs: React.FC = () => {
  const dispatch = useDispatch<any>();

  // Get auth state to pre-fill user information
  const authUser = useSelector((state: any) => state.Login.user);

  // Get contact messages state
  const { loading, success, error, message } = useSelector((state: any) => state.ContactMessages);

  // Form state
  const [formData, setFormData] = useState({
    fullName: authUser?.username || '',
    email: authUser?.email || '',
    subject: 'Feedback',
    content: '',
  });

  const [errors, setErrors] = useState<any>({});

  // Subject options
  const subjectOptions = [
    { value: 'Feedback', label: 'Feedback' },
    { value: 'Feature Request', label: 'Feature Request' },
    { value: 'Technical Support', label: 'Technical Support' },
    { value: 'Billing Issue', label: 'Billing Issue' },
    { value: 'Other', label: 'Other' },
  ];

  useEffect(() => {
    // Clear state when component unmounts
    return () => {
      dispatch(clearState());
    };
  }, [dispatch]);

  useEffect(() => {
    // Reset form after successful submission
    if (success) {
      setFormData({
        fullName: authUser?.username || '',
        email: authUser?.email || '',
        subject: 'Feedback',
        content: '',
      });
      setErrors({});
    }
  }, [success, authUser]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev: any) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleContentChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      content: value,
    }));
    // Clear error for content field
    if (errors.content) {
      setErrors((prev: any) => ({
        ...prev,
        content: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    if (!formData.subject) {
      newErrors.subject = 'Please select a subject';
    }

    // Strip HTML tags to check if content is empty
    const strippedContent = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!strippedContent) {
      newErrors.content = 'Message content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare payload
    const payload = {
      userId: authUser?.userId || null,
      fullName: formData.fullName,
      email: formData.email,
      subject: formData.subject,
      content: formData.content,
    };

    // Dispatch action
    dispatch(submitContactMessage(payload));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={8} xl={7}>
              <Card>
                <CardHeader className="bg-soft-primary">
                  <h4 className="card-title mb-0">Contact Us</h4>
                </CardHeader>
                <CardBody>
                  <p className="text-muted mb-3">
                    Have feedback, questions, or need support? We'd love to hear from you! Fill out
                    the form below and we'll get back to you as soon as possible.
                  </p>

                  {/* User Info Display */}
                  {formData.fullName && formData.email && (
                    <div className="alert alert-info mb-4" role="alert">
                      <i className="mdi mdi-account-circle me-2"></i>
                      <strong>Sending as:</strong> {formData.fullName} ({formData.email})
                    </div>
                  )}

                  {/* Success Alert */}
                  {success && message && (
                    <Alert color="success" className="alert-dismissible fade show">
                      <i className="mdi mdi-check-circle me-2"></i>
                      {message}
                    </Alert>
                  )}

                  {/* Error Alert */}
                  {error && (
                    <Alert color="danger" className="alert-dismissible fade show">
                      <i className="mdi mdi-alert-circle me-2"></i>
                      {error}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <FormGroup className="mb-3">
                      <Label for="subject">
                        Subject <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        invalid={!!errors.subject}
                      >
                        {subjectOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                      {errors.subject && (
                        <div className="invalid-feedback d-block">{errors.subject}</div>
                      )}
                    </FormGroup>

                    <FormGroup className="mb-4">
                      <Label for="content">
                        Message <span className="text-danger">*</span>
                      </Label>
                      <div style={{ border: '1px solid #ced4da', borderRadius: '0.25rem' }}>
                        <CKEditor
                          editor={ClassicEditor}
                          data={formData.content}
                          onChange={(event, editor) => {
                            const data = editor.getData();
                            handleContentChange(data);
                          }}
                          config={{
                            licenseKey: 'GPL',
                            placeholder: 'Write your message here...',
                            toolbar: [
                              'heading', '|',
                              'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
                              'blockQuote', 'insertTable', '|',
                              'undo', 'redo'
                            ]
                          }}
                        />
                      </div>
                      {errors.content && (
                        <div className="text-danger small mt-1">{errors.content}</div>
                      )}
                    </FormGroup>

                    <div className="text-end">
                      <Button
                        type="button"
                        color="secondary"
                        className="me-2"
                        onClick={() => {
                          setFormData({
                            fullName: authUser?.username || '',
                            email: authUser?.email || '',
                            subject: 'Feedback',
                            content: '',
                          });
                          setErrors({});
                          dispatch(clearState());
                        }}
                      >
                        Reset
                      </Button>
                      <Button type="submit" color="primary" disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="mdi mdi-send me-1"></i>
                            Submit Message
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>

                  <div className="mt-4 pt-4 border-top">
                    <Row>
                      <Col md={4} className="text-center mb-3 mb-md-0">
                        <div className="text-primary mb-2">
                          <i className="mdi mdi-email-outline" style={{ fontSize: '24px' }}></i>
                        </div>
                        <h6 className="mb-1">Email</h6>
                        <p className="text-muted mb-0">info@integr.dev</p>
                      </Col>
                      <Col md={4} className="text-center mb-3 mb-md-0">
                        <div className="text-primary mb-2">
                          <i className="mdi mdi-file-document-outline" style={{ fontSize: '24px' }}></i>
                        </div>
                        <h6 className="mb-1">Documentation</h6>
                        <p className="text-muted mb-0">
                          <a href="https://integr.dev/documentation" target="_blank" rel="noopener noreferrer">
                            View Docs
                          </a>
                        </p>
                      </Col>
                      <Col md={4} className="text-center">
                        <div className="text-primary mb-2">
                          <i className="mdi mdi-clock-outline" style={{ fontSize: '24px' }}></i>
                        </div>
                        <h6 className="mb-1">Response Time</h6>
                        <p className="text-muted mb-0">Within 24 hours</p>
                      </Col>
                    </Row>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ContactUs;
